import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  TrendingUp,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Calculator,
} from 'lucide-react';
import productService from '../services/productService';
import authService from '../services/authService';
import Layout from '../components/Layout';
import AdvancedSearch from '../components/AdvancedSearch';
import ProductsOverview from '../components/ProductsOverview';
import PriceHistoryDrawer from '../components/PriceHistoryDrawer';
import { CostEstimationDialog } from '../components/CostEstimation';

const ProductsPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_count: 0,
    total_pages: 0
  });
  const [filters, setFilters] = useState({
    name: '',
    category: '',
    product_id: '',
    part_number: '',
    client_ref_number: '',
    material: '',
    coating: '',
    stock_status: '',
    price_range: { min: '', max: '' },
    tip_diameter_range: { min: '', max: '' },
    total_length_range: { min: '', max: '' },
    degree_range: { min: '', max: '' }
  });
  const [sort, setSort] = useState({ by: 'name', order: 'asc' });
  const [searchMode, setSearchMode] = useState('basic');
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [userRole, setUserRole] = useState('');
  const [tradeTypeFilter, setTradeTypeFilter] = useState('all');
  const [priceHistoryProduct, setPriceHistoryProduct] = useState(null);
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [costEstimationProduct, setCostEstimationProduct] = useState(null);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      let response;
      if (searchMode === 'advanced' || globalSearch.trim()) {
        const searchCriteria = {
          query: globalSearch.trim(),
          filters: {
            ...filters,
            price_min: filters.price_range.min ? parseFloat(filters.price_range.min) : undefined,
            price_max: filters.price_range.max ? parseFloat(filters.price_range.max) : undefined,
            tip_diameter_min: filters.tip_diameter_range?.min ? parseFloat(filters.tip_diameter_range.min) : undefined,
            tip_diameter_max: filters.tip_diameter_range?.max ? parseFloat(filters.tip_diameter_range.max) : undefined,
            total_length_min: filters.total_length_range?.min ? parseFloat(filters.total_length_range.min) : undefined,
            total_length_max: filters.total_length_range?.max ? parseFloat(filters.total_length_range.max) : undefined,
            degree_min: filters.degree_range?.min ? parseFloat(filters.degree_range.min) : undefined,
            degree_max: filters.degree_range?.max ? parseFloat(filters.degree_range.max) : undefined,
          },
          sort: { by: sort.by, order: sort.order },
          page: pagination.page,
          page_size: pagination.page_size
        };
        Object.keys(searchCriteria.filters).forEach(key => {
          if (!searchCriteria.filters[key] && searchCriteria.filters[key] !== 0) {
            delete searchCriteria.filters[key];
          }
        });
        response = await productService.advancedSearch(searchCriteria);
      } else {
        response = await productService.getProducts(
          pagination.page,
          pagination.page_size,
          sort.by,
          sort.order,
          filters
        );
      }
      setProducts(response.products);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size, sort.by, sort.order, filters, searchMode, globalSearch]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await productService.getCategories();
      setCategories(response.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const response = await productService.getProductStats();
      setStats(response);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadStats();
  }, [loadProducts, loadCategories, loadStats]);

  useEffect(() => {
    const user = authService.getCurrentUser();
    setUserRole(user?.role || '');
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleAdvancedFilterChange = (filterName, value) => {
    if (filterName.includes('.')) {
      const [parent, child] = filterName.split('.');
      setFilters(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFilters(prev => ({ ...prev, [filterName]: value }));
    }
  };

  const handleGlobalSearchChange = useCallback(async (searchValue) => {
    setGlobalSearch(searchValue);
    if (searchValue.trim().length >= 2) {
      setSearchLoading(true);
      try {
        const response = await productService.searchProducts(searchValue, 1, 5);
        const suggestions = response.products.map(p => ({
          label: `${p.name} (${p.product_id})`,
          value: p.name,
          productId: p.product_id,
          category: p.category
        }));
        setSearchSuggestions(suggestions);
      } catch (err) {
        console.error('Search suggestions failed:', err);
        setSearchSuggestions([]);
      } finally {
        setSearchLoading(false);
      }
    } else {
      setSearchSuggestions([]);
    }
  }, []);

  const handleFilterSubmit = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadProducts();
  };

  const handleClearFilter = (filterName) => {
    setFilters(prev => ({ ...prev, [filterName]: '' }));
  };

  const handleClearAllFilters = () => {
    setFilters({
      name: '',
      category: '',
      product_id: '',
      part_number: '',
      client_ref_number: '',
      material: '',
      coating: '',
      stock_status: '',
      price_range: { min: '', max: '' },
      tip_diameter_range: { min: '', max: '' },
      total_length_range: { min: '', max: '' },
      degree_range: { min: '', max: '' }
    });
    setGlobalSearch('');
    setSearchSuggestions([]);
    setTradeTypeFilter('all');
  };

  const handleExportProducts = async () => {
    try {
      await productService.exportProductList(products, 'csv');
    } catch (err) {
      setError('Failed to export products');
    }
  };

  const handleSearchModeToggle = () => {
    setSearchMode(prev => prev === 'basic' ? 'advanced' : 'basic');
    if (searchMode === 'advanced') {
      setAdvancedFiltersOpen(false);
      setGlobalSearch('');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = (property) => {
    const isAsc = sort.by === property && sort.order === 'asc';
    setSort({ by: property, order: isAsc ? 'desc' : 'asc' });
  };

  const handleShowPriceHistory = (product) => {
    setPriceHistoryProduct(product);
    setShowPriceHistory(true);
  };

  const handleClosePriceHistory = () => {
    setShowPriceHistory(false);
    setPriceHistoryProduct(null);
  };

  const handleCostEstimation = (product) => {
    setCostEstimationProduct(product);
  };

  const handleCloseCostEstimation = () => {
    setCostEstimationProduct(null);
  };

  const handleSearchBySpecification = () => {
    // Switch to advanced search mode if not already
    if (searchMode !== 'advanced') {
      setSearchMode('advanced');
    }
    // Focus on the search input with placeholder for specification
    setGlobalSearch('');
    // Open advanced filters to show more options
    setAdvancedFiltersOpen(true);
  };



  const headCells = [
    { id: 'name', label: '产品名称' },
    { id: 'product_id', label: '产品编号' },
    { id: 'client_ref_number', label: '物料编码' },
    { id: 'category', label: '分类' },
    { id: 'specification', label: '规格' },
    { id: 'status', label: '状态' },
    { id: 'actions', label: '操作', sortable: false },
  ];

  const SortIcon = ({ columnId }) => {
    if (sort.by !== columnId) return <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />;
    return sort.order === 'asc'
      ? <ArrowUp className="ml-1 h-4 w-4" />
      : <ArrowDown className="ml-1 h-4 w-4" />;
  };

  return (
    <Layout title="产品管理" activeTab="products">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Compact Products Overview */}
        <ProductsOverview
          stats={stats}
          onClearAllFilters={handleClearAllFilters}
          onSetFilter={setFilters}
          onSetSearchMode={setSearchMode}
          onSetAdvancedFiltersOpen={setAdvancedFiltersOpen}
          onFilterSubmit={handleFilterSubmit}
          onExportProducts={handleExportProducts}
        />

        {/* Enhanced Search & Filters */}
        <AdvancedSearch
          searchMode={searchMode}
          filters={filters}
          categories={categories}
          searchSuggestions={searchSuggestions}
          searchLoading={searchLoading}
          globalSearch={globalSearch}
          onSearchModeToggle={handleSearchModeToggle}
          onFiltersChange={handleFilterChange}
          onAdvancedFilterChange={handleAdvancedFilterChange}
          onGlobalSearchChange={handleGlobalSearchChange}
          onFilterSubmit={handleFilterSubmit}
          onClearFilter={handleClearFilter}
          onClearAllFilters={handleClearAllFilters}
          onExport={handleExportProducts}
          advancedFiltersOpen={advancedFiltersOpen}
          setAdvancedFiltersOpen={setAdvancedFiltersOpen}
          loading={loading}
          pageType="products"
          userRole={userRole}
          tradeTypeFilter={tradeTypeFilter}
          onTradeTypeFilterChange={setTradeTypeFilter}
          onSearchBySpecification={handleSearchBySpecification}
        />
        />


        {/* Create New Product Button */}
        <div className="flex justify-end mb-4">
          <Button onClick={() => navigate('/products/new')} className="px-6">
            <Plus className="mr-2 h-4 w-4" />
            新建产品
          </Button>
        </div>

        {/* Products Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        {headCells.map((headCell) => (
                          <th
                            key={headCell.id}
                            className="px-4 py-3 text-left font-semibold text-foreground"
                          >
                            {headCell.sortable !== false ? (
                              <button
                                className="flex items-center hover:text-primary transition-colors"
                                onClick={() => handleSort(headCell.id)}
                              >
                                {headCell.label}
                                <SortIcon columnId={headCell.id} />
                              </button>
                            ) : (
                              headCell.label
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {products.map((product, index) => (
                        <tr key={product._id ? String(product._id) : `product-${index}`} className="hover:bg-muted/30">
                          <td className="px-4 py-3">{product.name}</td>
                          <td className="px-4 py-3">
                            <button
                              className="text-primary underline hover:text-primary/80 transition-colors"
                              onClick={() => navigate(`/products/${product._id}/edit`)}
                            >
                              {product.product_id}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {product.client_ref_number || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline">{product.category}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <span className="max-w-[200px] truncate block">
                              {product.specification || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={product.status === 'active' ? 'default' : 'secondary'}>
                              {product.status || 'active'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleShowPriceHistory(product)}
                                title="查看价格历史"
                              >
                                <TrendingUp className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleCostEstimation(product)}
                                title="成本估算"
                                disabled={!product.tip_diameter || !product.total_length}
                              >
                                <Calculator className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {products.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                            {globalSearch.trim() ? `未找到 "${globalSearch}" 相关产品` : '未找到产品'}
                            <div className="mt-2">
                              <Button variant="link" size="sm" onClick={handleClearAllFilters}>
                                清除所有筛选
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {pagination.total_pages > 1 && (
                  <div className="flex justify-center py-4 border-t">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                      >
                        上一页
                      </Button>
                      <span className="text-sm text-muted-foreground px-4">
                        第 {pagination.page} / {pagination.total_pages} 页
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.total_pages}
                      >
                        下一页
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <p className="mt-4 text-sm text-muted-foreground">
          显示 {products.length} / {pagination.total_count} 个产品
        </p>
      </div>

      {/* Price History Drawer */}
      {priceHistoryProduct && (
        <PriceHistoryDrawer
          open={showPriceHistory}
          onClose={handleClosePriceHistory}
          initialMode="single"
          productId={priceHistoryProduct.product_id || ''}
          productName={priceHistoryProduct.name || ''}
          initialTipDiameter={priceHistoryProduct.tip_diameter || ''}
          initialTotalLength={priceHistoryProduct.total_length || ''}
          initialCategory={priceHistoryProduct.category || ''}
        />
      )}

      {/* Cost Estimation Dialog */}
      <CostEstimationDialog
        open={!!costEstimationProduct}
        onClose={handleCloseCostEstimation}
        product={costEstimationProduct}
      />
    </Layout>
  );
};

export default ProductsPage;
